import { CdkDragMove } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, effect, ElementRef, EventEmitter, HostListener, inject, Input, Output, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { addDays, addHours, addMonths, addWeeks, addYears, differenceInDays, differenceInHours, differenceInWeeks, differenceInYears, endOfMonth, format, getDate, getDaysInMonth, getHours, getISOWeek, isAfter, isBefore, isLeapYear, startOfDay, startOfHour, startOfMonth, startOfWeek, startOfYear } from 'date-fns';
import { BehaviorSubject, Subject } from 'rxjs';
import { take } from 'rxjs/operators';

// Services
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { PlanningService } from 'src/app/services/api/plannings/planning.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { PlanningStore } from 'src/app/services/stores/planning.store';

// Interfaces
import { Attachment } from 'src/app/interfaces/api/attachment';
import { Fragment } from 'src/app/interfaces/api/fragment';
import { Metric } from 'src/app/interfaces/api/metric';
import { Planning } from 'src/app/interfaces/api/planning';
import { Project } from 'src/app/interfaces/api/project';
import { PlanningTask } from 'src/app/interfaces/api/task';
import { Unit } from 'src/app/interfaces/api/unit';
import { User } from 'src/app/interfaces/api/user';
import { Config } from 'src/app/interfaces/shared/config';
import { CachedPeriod, MouseState, TimelinePeriod, ViewState } from 'src/app/interfaces/shared/planning-view';

// Classes
import { enUS, fr } from 'date-fns/locale';
import { PlanningTools } from 'src/app/classes/planning/planning-tools';
import { Pop } from 'src/app/classes/shared/pop';

// Components
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { ButtonComponent, SpinnerComponent } from 'ui-library';

@Component({
  selector: 'app-planning-view',
  imports: [
    CommonModule,
    MatIconModule,
    TranslateModule,
    SpinnerComponent,
    NoDataComponent,
    ButtonComponent
  ],
  standalone: true,
  templateUrl: './planning-view.component.html',
  styleUrls: ['./planning-view.component.scss']
})

export class PlanningViewComponent {
  // Services
  private readonly planningStore: PlanningStore = inject(PlanningStore);
  private readonly planningService: PlanningService = inject(PlanningService);
  private readonly metricService: MetricService = inject(MetricService);
  private readonly attachmentService: AttachmentService = inject(AttachmentService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly pop: Pop = inject(Pop);
  private readonly planningTools: PlanningTools = inject(PlanningTools);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  // Components API
  @ViewChild('timeline') timeline!: ElementRef;
  @ViewChild('board') board!: ElementRef;
  @ViewChild("resize", { static: false }) resize!: ElementRef;
  @ViewChild("graph", { static: false }) graph!: ElementRef;
  @Input() showUnlinked: Signal<boolean> = signal(false);
  @Input("width") ganttWidth!: Signal<number>;
  @Output("onCurrentTaskChanged") updateCurrentTask: EventEmitter<PlanningTask> = new EventEmitter();

  // Signals
  periodHeight: WritableSignal<number> = signal<number>(48);
  state: WritableSignal<ViewState> = signal<ViewState>({
    showHours: false,
    showDays: false,
    showWeeks: false,
    showMonths: true,
    showYears: true
  });
  // readonly planning: Signal<Planning> = this.planningStore.planning;
  // readonly tasks: Signal<PlanningTask[]> = this.planningStore.tasks;
  // readonly metrics: Signal<Metric[]> = this.planningStore.metrics;
  // readonly currentTask: Signal<PlanningTask | null> = this.planningStore.currentTask;
  // readonly selectedMetrics: Signal<Metric[]> = this.planningStore.selectedMetrics;
  // readonly selectedFragments: Signal<Fragment[]> = this.planningStore.selectedFragments;
  // readonly selectedTasks: Signal<PlanningTask[]> = this.planningStore.selectedTasks;

  // Variables
  user!: User;
  project!: Project;

  planning = this.planningStore.planning();
  tasks = this.planningStore.tasks();
  metrics = this.planningStore.metrics();
  fragments: Fragment[] = [];
  currentTask = this.planningStore.currentTask();
  selectedFragments = this.planningStore.selectedFragments();
  selectedTasks = this.planningStore.selectedTasks();

  currentDate = new Date().toISOString();
  filteredTasks: PlanningTask[] = [];

  destroy$ = new Subject<void>();

  years: TimelinePeriod[] = [];
  months: TimelinePeriod[] = [];
  days: TimelinePeriod[] = [];
  weeks: TimelinePeriod[] = [];
  hours: TimelinePeriod[] = [];
  minutes: TimelinePeriod[] = [];

  startDate = startOfMonth(new Date()).toISOString();
  endDate = endOfMonth(new Date()).toISOString();
  startOfDay = startOfDay(new Date());
  startOfWeek = startOfWeek(new Date());
  timelineMoment = new Date();
  hourSize = 0.2;//px
  daySize = this.hourSize * 24;
  weekSize = this.daySize * 7;
  dayCount = 0;
  weekLeft = 0;
  graphWidth: number = 200;
  timelineWidth = 100;
  scale: number = 1;
  coef: number = 1;
  darkBarTop: number = 0;
  dateBarLeft: number = 0;
  darkBarHeight: number = 0;
  darkBarBottom: number = 0;
  minuteWidth: number = 10;
  hoverInfo: boolean = false;
  keyPressed = "";
  darkBarVisible = true;
  initialScales: string[] = ["full", "year", 'semester', 'quarter', 'month', 'halfMonth', 'week', 'halfWeek', 'day']
  scaleDayCount: number[] = [365 * 3, 365, 30 * 3 + 3 * 31, 30 * 3, 31, 15, 7, 3, 1];

  scales: string[] = [...this.initialScales];
  scaleIndex: number = 0;
  hourSizes: Record<string, number> = {};
  calendarWidth: number = 500;
  hourWidth: number = 50;
  dayCounts: Record<string, number> = { full: 365 * 3, year: 365, semester: 30 * 3 + 3 * 31, quarter: 30 * 3, month: 31, halfMonth: 15, week: 7, halfWeek: 3, day: 1 }

  boardWidth: number = 1100;
  contentWidth: number = 500;

  currentScale: string = "full";
  scrollLeft: number = 0;
  scrollTop: number = 0;
  graphHeight: number = 100;
  lineHeight: number = 30;
  treeWidth: number = 280;
  barDeltaX: number = 0;
  startBarDeltaX: number = 0;
  startBarPosition: number = 0;
  dateBarPosition: number = 230;
  isSidebarOpen: boolean = false;

  timelineBound = { x: 205, y: 0 };
  loadedDates: [string, string][] = [];
  mouse: MouseState = { x: 0, y: 0, isDown: false, dX: 0, dY: 0 };
  dragged!: string;

  units: Unit[] = [];
  updatingPlanning: boolean = false;
  loading3D: boolean = false;
  helpLink = 'https://cad42.atlassian.net/wiki/x/AYBhKQ';

  //player 
  playerTimeText: any;
  playerValue!: BehaviorSubject<any>;
  showGantt = true;
  loadingFragment!: boolean;
  fragmentById: Record<string, Fragment> = {};
  model!: object;
  parsingFragment!: boolean;
  planningExt!: Planning;
  autolinkAvailable!: boolean;
  isEditing = false;
  tasksOrder: number[] = [];

  session: Record<string, string> = {};
  attachments: Attachment[] = [];
  limit: number[] = [];
  offset: number[] = [];
  scrollLoading: boolean = false;
  metricCounts: Record<string, number> = {};
  metricsByTask: Record<string, Metric[]> = {}; // { [taskId]: metrics[] }
  scrollThreshold = 200;
  previousTasksState!: PlanningTask[];
  draggedTaskIndex: number | null = null;
  showDropLine: boolean = false;
  hoverIndex: number | null = null;
  isNesting: boolean = false;
  isDragEnabled: boolean = false;
  firstZoom!: boolean;
  cachedDays!: CachedPeriod;
  cachedMonths!: CachedPeriod;
  cachedYears!: CachedPeriod;
  cachedWeeks!: CachedPeriod;
  cachedHours!: CachedPeriod;
  cachedMinutes!: CachedPeriod;
  updateTaskBusy: boolean = false;
  metricMap: Record<string, Metric> = {};
  dropZoneState: 'none' | 'before' | 'after' | 'child-start' | 'child-end' = 'none';

  // Listeners
  @HostListener('mousemove', ['$event']) // for window scroll events
  onMouseMove(event: MouseEvent) {
    if (this.dragged && this.dragged.includes("mat-input-")) return;

    window.getSelection()?.removeAllRanges()

    this.mouse.x = event.x;
    this.mouse.y = event.y;

    if (event.buttons == 0)
      this.mouse.isDown = false;

    if (this.mouse.isDown) {
      if (this.isDragEnabled) return;

      if (this.dragged == 'resizeBar') {
        this.treeWidth = Math.max(Math.floor(this.treeWidth + event.movementX), 0);
        this.boardWidth = this.ganttWidth() - this.treeWidth;
        this.updateHourSizes();
        this.graphWidth = this.dayCount * this.hourSize * 24;
        this.graphWidth = Math.max(this.graphWidth, this.boardWidth);
        this.setLines();
      } else if (this.dragged == 'resize-handler') {
        this.boardWidth = Math.max(Math.floor(this.boardWidth + event.movementX), 0);
        const maxScrollLeft = Math.max(0, this.graphWidth - this.boardWidth);

        if (this.scrollLeft > maxScrollLeft) {
          this.scrollLeft = maxScrollLeft;
        }

        this.updateHourSizes();
        this.graphWidth = this.dayCount * this.hourSize * 24;
        this.graphWidth = Math.max(this.graphWidth, this.boardWidth);
        this.setLines();
      } else if (this.dragged == 'date-bar') {
        this.dateBarLeft = Math.max(this.dateBarLeft + event.movementX, 0);
        let date = this.getDateFromLeft(this.dateBarLeft)

        this.startOfDay = startOfDay(new Date(date));
        this.startOfWeek = startOfWeek(new Date(date));

        this.planningStore.setCurrentDate(date);
        this.updateTasks();
        this.currentDate = date;
      } else {
        const maxScrollLeft = Math.max(0, this.graphWidth - this.boardWidth);

        this.scrollLeft = Math.max(
          Math.min(Math.floor(this.scrollLeft - event.movementX * 2), maxScrollLeft),
          0
        );

        this.graph.nativeElement.scrollTop -= event.movementY * 2;
        this.scrollTop = this.graph.nativeElement.scrollTop;
      }
    }
  }

  @HostListener('mouseup', ['$event']) // for window scroll events
  onMouseUp(event: MouseEvent) {
    this.mouse.isDown = false;
    this.planningStore.setCurrentDate(this.currentDate);
    this.setLines();
  }

  @HostListener('mousedown', ['$event']) // for window scroll events
  onMouseDown(event: MouseEvent) {
    this.mouse.isDown = true;
    this.mouse.dX = event.x;
    this.mouse.dY = event.y;
    this.timelineBound = this.timeline.nativeElement.getBoundingClientRect();
    this.dragged = (event.target as HTMLElement)?.id || '';
  }

  // On the planning only (its .line elements): a document listener kept every wheel of the app checking it, even in the route cache.
  @HostListener('mousewheel', ['$event'])
  onMouseWheel(event: WheelEvent) {
    if ((event.target as HTMLElement)?.className?.includes('line')) {
      if (event.deltaY < 0) {
        this.setZoom(1, event);
      } else if (event.deltaY > 0) {
        this.setZoom(-1, event);
      }
    }
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(event: MouseEvent) {
    event.preventDefault();
    let prevHourSize = this.hourSize;
    this.updateHourSizes();
    this.setZoom(0, null, prevHourSize);
  }

  constructor() {
    this.user = this.authService.currentUserValue;
    this.playerValue = new BehaviorSubject(0);

    this.projectService.currentProject.pipe(take(1)).subscribe(project => {
      this.project = project
    })

    // 📊 Effect to select current metric and update date bar position on metric change
    effect(() => {
      let selectedMetrics = this.planningStore.selectedMetrics();
      let task = this.tasks.find(t => selectedMetrics[0]?.payload['taskId'] == t.id)
      if (selectedMetrics.length == 1 && task) {
        this.setActiveTask(task)
      }
    });

    // 📅 Effect to update current date and date bar position
    effect(() => {
      const date = this.planningStore.currentDate();

      if (date) {
        const dateStr = new Date(date).toISOString();
        this.currentDate = dateStr;

        this.startOfDay = startOfDay(new Date(date));
        this.startOfWeek = startOfWeek(new Date(date));

        this.updateTasks();
        this.setLines();
      }
    });

    // ✅ Effect to filter tasks based on unlinked status
    effect(() => {
      const showUnlinked = this.showUnlinked();
      const sorted = this.planningStore.sortedTasks();
      this.filteredTasks = showUnlinked ? sorted : sorted.filter(t => t.fragments?.length);
    });

    // 🔗 Effect to update fragments and refresh planning on fragments change
    effect(() => {
      const tasks = this.planningStore.sortedTasks();

      tasks.forEach(t => {
        if (isBefore(t.startDate!.toString(), this.startDate)) {
          this.startDate = t.startDate!.toString();
        }

        if (t.endDate && isAfter(t.endDate, this.endDate)) this.endDate = t.endDate.toString();
        this.updateTask(t);
      })

      this.tasks = tasks;
      this.refreshPlanning();
    });

    // ✨ Effect to update selected fragment highlighting
    effect(() => {
      const frags = this.planningStore.selectedFragments();

      if (frags && frags.length == 1) {
        this.tasks.forEach(t => {
          if (t.fragments && t.fragments.includes(frags[0]?.id || '')) {
            t.backgroundColor = "var(--content-background-color-hover)";
          } else {
            t.backgroundColor = "var(--content-background-color)";
          }
        })
      }
    });

    // 📐 Effect to update board width and hour sizes on resize-bar
    effect(() => {
      const width = this.ganttWidth();
      if (!this.board) return;

      this.boardWidth = width - this.treeWidth;
      this.updateHourSizes();
      this.graphWidth = this.dayCount * this.hourSize * 24;
      this.graphWidth = Math.max(this.graphWidth, this.boardWidth);
      this.setLines();
    });

    // 📏 Effect periodHeight based on visible lines
    effect(() => {
      const currentState = this.state();

      const visibleLines = [
        currentState.showYears,
        currentState.showMonths,
        currentState.showWeeks,
        currentState.showDays,
        currentState.showHours
      ].filter(Boolean).length;

      const reduction = visibleLines === 3 ? 1 : 0;
      const height = visibleLines * 16 - reduction;
      this.periodHeight.set(Math.max(16, height));
    });
  }

  refreshPlanning() {
    this.startDate = startOfMonth(new Date(this.startDate)).toISOString();
    this.endDate = endOfMonth(new Date(this.endDate)).toISOString();
    this.dayCount = differenceInDays(new Date(this.endDate), new Date(this.startDate)) + 1;
    this.graphWidth = this.dayCount * this.hourSize * 24;
    this.graphWidth = Math.max(this.graphWidth, this.boardWidth);
    this.graphHeight = this.filteredTasks.length * this.lineHeight;

    this.getOrderedTasks();
    this.setTreeGraph();

    this.updateHourSizes();

    if (!this.board) return;

    const maxScrollLeft = Math.max(0, this.graphWidth - this.boardWidth);
    this.scrollLeft = maxScrollLeft;

    this.initializeTasksOrder();
    this.setLines();
  }

  setTreeGraph() {
    const setIndent = (tasks: PlanningTask[], index: number) => {
      tasks.forEach(t => {
        t.indent = index;

        if (t.children && t.children.length) {
          t.color = Config.timelineColors.dark;
          t.class = "parent";
          setIndent(t.children, index + 1);
        }
      });
    };

    const toKey = (value: unknown): string => String(Number(value ?? 0));
    const byNumber = new Map<string, PlanningTask>();

    this.tasks.forEach((t, i) => {
      t.index = i;
      t.parentTask = null;
      t.children = [];
      t.groupMetricCount = 0;
      byNumber.set(toKey(t.number), t);
    });

    this.tasks.forEach(t => {
      if (t.parent) {
        const parent = byNumber.get(toKey(t.parent));

        if (parent) {
          parent.children?.push(t);
          t.parentTask = parent;

          if (t.text && t.metrics?.length) {
            if (parent.groupMetricCount == undefined) {
              parent.groupMetricCount = 0;
            }
            parent.groupMetricCount += t.metrics.length;
          }
        }
      }

      t.color = Config.timelineColors.success;

      if (t.startDate) {
        t.left = this.getWidthFromStartDate(t.startDate.toString());
      }

      if (t.startDate && t.endDate) {
        t.width = this.getWidthFromDates(t.startDate.toString(), t.endDate.toString());
      }

      if (t.metrics && t.metrics.length) {
        for (let m of t.metrics) {
          if (m.payload['startDate'] && m.payload['endDate']) {
            m.left = this.getWidthFromStartDate(m.payload['startDate']);
            m.width = this.getWidthFromDates(m.payload['startDate'], m.payload['endDate']);
          }
        }

        t.firstUpdate = t.metrics[0]?.createdDate || '';
        t.lastUpdate = t.metrics[t.metrics.length - 1]?.createdDate || '';

        if (t.firstUpdate) {
          t.firstUpdateLeft = this.getWidthFromStartDate(t.firstUpdate.toString());
        }

        if (t.firstUpdate && t.lastUpdate) {
          t.firstUpdateWidth = this.getWidthFromDates(t.firstUpdate.toString(), t.lastUpdate.toString());
        }
      }
    });

    this.tasks.forEach(t => {
      let parent = t.parentTask;

      if (!parent || parent.index == 0) return;

      while (parent && parent.index && parent.index > 0) {
        if (t.groupMetricCount) {
          if (parent.groupMetricCount == undefined) {
            parent.groupMetricCount = 0;
          }

          parent.groupMetricCount += t.groupMetricCount;
        }

        parent = parent.parentTask!;
      }
    });

    this.tasks.forEach(t => (t.indent = 0));

    const rootTasks = this.tasks.filter(t => {
      const parentValue = t.parent ? Number(t.parent) : 0;
      return parentValue === 0;
    });

    setIndent(rootTasks, 0);
    this.updateTasks();
  }

  //update displayed task volume progress and color
  updateTask(task: PlanningTask) {
    if (!task.metrics?.length) return

    task.progress = 0;
    task.volumeProgress = 0;

    this.setLateBarWidth(task)
    this.planningTools.setTaskPriority(task, this.currentDate);

    if (task.priority && task.lastUpdate && isBefore(new Date(task.lastUpdate), new Date(this.currentDate))) return;

    for (let m of task.metrics) {
      if (isAfter(new Date(m.createdDate), new Date(this.currentDate))) break;

      if (m.payload['volume'])
        task.volumeProgress += parseFloat(m.payload['volume'] as string);
    }

    task.progress = task.volumeProgress / task.volume;
    task.volumeProgressText = task.volumeProgress.toFixed(2);
  }

  updateTasks() {
    this.tasks.forEach(t => {
      this.updateTask(t);
    })
  }

  updateHourSizes(): void {
    this.hourSizes = {};
    const startMoment = startOfMonth(new Date(this.startDate));
    const endMoment = endOfMonth(new Date(this.endDate));
    const numberOfDays = differenceInDays(endMoment, startMoment);

    this.scaleDayCount.forEach((dayCount: number, i: number) => {
      if (this.scales[i]) {
        this.hourSizes[this.scales[i]] = (this.boardWidth * 2) / (dayCount * 24);
      }
    });

    this.hourSize = this.hourSizes[this.currentScale!] || 1;
  }

  getFrustumDates() {
    let startDate = this.getDateFromLeft(this.scrollLeft);
    let elementBound = this.board.nativeElement.getBoundingClientRect();
    let endDate = this.getDateFromLeft(this.scrollLeft + elementBound.width);
    return { startDate, endDate }
  }

  updateMinuteLine(start: string, end: string) {
    if (this.cachedMinutes) {
      this.minutes = this.cachedMinutes.data;
      return;
    }

    this.minutes = [];
    const alignedStart = new Date(start);
    alignedStart.setSeconds(0, 0);
    const alignedEnd = new Date(end);
    alignedEnd.setSeconds(0, 0);
    const numberOfMinutes = Math.round((alignedEnd.getTime() - alignedStart.getTime()) / 60000);

    for (let i = 0; i <= numberOfMinutes; i++) {
      this.minutes.push({
        label: '',
        position: this.scrollLeft + i * (this.hourSize / 60)
      });
    }

    this.cachedMinutes = {
      start: alignedStart.toISOString(),
      end: alignedEnd.toISOString(),
      data: this.minutes
    };
  }

  updateHourLine(start: string, end: string) {
    this.hours = [];
    const alignedStart = startOfHour(new Date(start));
    const alignedEnd = startOfHour(new Date(end));
    const numberOfHours = differenceInHours(alignedEnd, alignedStart);

    const startDate = startOfDay(new Date(this.startDate));
    const numberOfDaysFromStart = differenceInDays(alignedStart, startDate);

    const dayWidth = 24 * this.hourSize;
    let position = numberOfDaysFromStart * dayWidth;

    for (let i = 0; i <= numberOfHours; i++) {
      const currentHour = addHours(alignedStart, i);

      this.hours.push({
        label: getHours(currentHour) + 'h',
        position: Math.round(position)
      });

      position += this.hourSize;
      position = Math.round(position);
    }
  }

  updateDayLine(start: string, end: string) {
    this.days = [];
    const startDate = startOfDay(new Date(this.startDate));
    const alignedStart = startOfDay(new Date(start));
    const alignedEnd = startOfDay(new Date(end));
    const numberOfDays = differenceInDays(alignedEnd, alignedStart);
    const numberOfDaysFromStart = differenceInDays(alignedStart, startDate);

    const dayWidth = 24 * this.hourSize;
    let position = numberOfDaysFromStart * dayWidth;
    const locale = this.user.language?.startsWith('fr') ? fr : enUS;

    for (let i = 0; i <= numberOfDays; i++) {
      const currentDay = addDays(alignedStart, i);
      let label: string;

      if (dayWidth < 50) {
        label = getDate(currentDay).toString();
      } else if (dayWidth < 100) {
        label = format(currentDay, 'EEE d', { locale });
      } else {
        label = format(currentDay, 'EEEE d', { locale });
      }

      this.days.push({
        label,
        position: Math.round(position)
      });

      position += dayWidth;
      position = Math.round(position);
    }
  }

  updateWeekLine(start: string, end: string) {
    this.weeks = [];
    const alignedStart = startOfWeek(new Date(start));
    const alignedEnd = startOfWeek(new Date(end));
    const numberOfWeeks = differenceInWeeks(alignedEnd, alignedStart) + 1;

    const startDate = startOfDay(new Date(this.startDate));
    const numberOfDaysFromStart = differenceInDays(alignedStart, startDate);

    const dayWidth = 24 * this.hourSize;
    let position = numberOfDaysFromStart * dayWidth;
    const size = 7 * 24 * this.hourSize;

    for (let i = 0; i < numberOfWeeks; i++) {
      const currentWeekStart = addWeeks(alignedStart, i);
      const weekNumber = getISOWeek(currentWeekStart);
      const weekPrefix = this.translate.instant('date.week');

      const label = size < 700 ? `Sem. ${weekNumber}` : `${weekPrefix} ${weekNumber}`;

      this.weeks.push({
        label,
        size,
        position: Math.round(position),
      });

      position += size;
      position = Math.round(position);
    }
  }

  updateMonthLine(start: string, end: string) {
    this.months = [];
    let position = 0;
    const dayWidth = 24 * this.hourSize;
    const locale = this.user.language?.startsWith('fr') ? fr : enUS;
    let i = 0;

    while (position < this.graphWidth) {
      const monthDate = addMonths(new Date(this.startDate), i);
      const dayCount = getDaysInMonth(monthDate);
      const size = dayCount * dayWidth;

      let label = size < 80 ? format(monthDate, 'MMM', { locale }) : format(monthDate, 'MMMM', { locale });
      label = label.charAt(0).toUpperCase() + label.slice(1);
      label = label.replace(".", "");

      this.months.push({
        label,
        size,
        position: Math.round(position)
      });

      position += size;
      position = Math.round(position);
      i++;
    }
  }

  updateYearLine(start: string, end: string) {
    this.years = [];
    const alignedStart = startOfYear(new Date(start));
    const alignedEnd = startOfYear(new Date(end));
    const numberOfYears = differenceInYears(alignedEnd, alignedStart) || 1;

    const startDate = startOfDay(new Date(this.startDate));
    const numberOfDaysFromStart = differenceInDays(alignedStart, startDate);
    const dayWidth = 24 * this.hourSize;
    let position = numberOfDaysFromStart * dayWidth;

    for (let i = 0; i < numberOfYears; i++) {
      const currentYearStart = addYears(alignedStart, i);
      const yearSize = isLeapYear(currentYearStart) ? 366 : 365;
      const size = yearSize * dayWidth;

      this.years.push({
        label: format(currentYearStart, 'yyyy'),
        size,
        position: Math.round(position)
      });

      position += size;
      position = Math.round(position);
    }
  }

  setLines() {
    let { startDate, endDate } = this.getFrustumDates();
    this.updateYearLine(this.startDate, this.endDate);
    this.updateMonthLine(this.startDate, this.endDate);

    if (this.isSmallerThan('semester')) {
      this.updateWeekLine(startDate, endDate);
    } else {
      this.state.update(s => ({ ...s, showWeeks: false }));
      this.weeks = [];
    }

    if (this.isSmallerThan('quarter')) {
      this.state.update(s => ({ ...s, showDays: true }));
      this.updateDayLine(startDate, endDate);
    } else {
      this.state.update(s => ({ ...s, showDays: false }));
      this.days = [];
    }

    if (this.isSmallerThan('day')) {
      this.state.update(s => ({ ...s, showHours: true }));
      this.updateHourLine(startDate, endDate);
    } else {
      this.state.update(s => ({ ...s, showHours: false }));
      this.hours = [];
    }

    // this.updateMinuteLine(startDate, endDate);
    this.state.update(s => ({ ...s, showDays: this.isSmallerThan('quarter') }));
    this.state.update(s => ({ ...s, showWeeks: this.isSmallerThan('semester') }));
    this.state.update(s => ({ ...s, showMonths: true })); //this.isSmallerThan('threeYears');
    this.state.update(s => ({ ...s, showYears: true }));//this.isGreaterThan('year');

    this.updateDateBar(this.currentDate);
    this.cdr.detectChanges();
  }

  updateDateBar(date: string) {
    this.dateBarLeft = this.getWidthFromStartDate(date) * this.hourSize * this.scale;
  }

  getDateFromLeft(left: number) {
    return addHours(new Date(this.startDate), left / this.hourSize / this.scale).toISOString();
  }

  getWidthFromDates(startDate: string, endDate: string) {
    const msPerHour = 3600000;
    const diff = (new Date(endDate).getTime() - new Date(startDate).getTime()) / msPerHour;
    return diff;
  }

  getWidthFromStartDate(startDate: string) {
    return this.getWidthFromDates(startOfHour(new Date(this.startDate)).toISOString(), startDate);
  }

  getScaled(value: number) {
    return value < 1 ? Math.ceil(value * this.hourSize) : value * this.hourSize;
  }

  selectFragments(task: PlanningTask) {
    const fragments = this.planningStore.getFragmentsByIds(task.fragments ?? [])
    this.planningStore.isolateFragments(fragments);
  }

  selectMetrics(task: PlanningTask) {
    let metrics = task.metrics
    this.planningStore.isolateMetrics(metrics ?? []);
  }

  animateScrollAndSize(startScrollLeft: number, endScrollLeft: number, startHourSize: number, endHourSize: number, duration: number) {
    return new Promise<void>((resolve) => {
      let startTime: number | null = null;
      const maxScrollLeft = Math.max(0, this.graphWidth - this.boardWidth);
      endScrollLeft = Math.min(endScrollLeft, maxScrollLeft);

      function easeOutSine(x: number) {
        return Math.sin((x * Math.PI) / 2);
      }

      if (duration > 0) {
        const animate = (timestamp: number) => {
          if (startTime === null) startTime = timestamp;
          const progress = timestamp - startTime;
          const progressRatio = Math.min(progress / duration, 1);
          const easedProgress = easeOutSine(progressRatio);

          const currentScrollLeft = startScrollLeft + (endScrollLeft - startScrollLeft) * easedProgress;
          const currentHourSize = startHourSize + (endHourSize - startHourSize) * easedProgress;

          this.scrollLeft = currentScrollLeft;
          this.hourSize = currentHourSize;

          if (progress < duration) {
            requestAnimationFrame(animate);
          } else {
            this.scrollLeft = endScrollLeft;
            this.hourSize = endHourSize;
            resolve();
          }
        };

        requestAnimationFrame(animate);
      } else {

        this.scrollLeft = startScrollLeft + (endScrollLeft - startScrollLeft);
        this.hourSize = startHourSize + (endHourSize - startHourSize);
        resolve();
      }
    });
  }

  async setZoom(val: number, event: MouseEvent | WheelEvent | null = null, prevHourSize: number | null = null, positionLeft: number | null = null) {
    this.updateDateBar(this.currentDate);

    if (this.hourSize != prevHourSize) {
      let boardBound = this.board.nativeElement.getBoundingClientRect();
      let boardLeft = boardBound.left;
      let mouseX = event ? event.x : 0;
      let mouseLeft = mouseX - boardLeft;

      if (mouseLeft < 0)
        mouseLeft = 0;

      prevHourSize = prevHourSize ?? this.hourSize;
      this.scaleIndex += val;

      if (this.scaleIndex == -1)
        this.scaleIndex = 0;

      if (this.scaleIndex >= this.scales.length)
        this.scaleIndex = this.scales.length - 1;

      this.scale = 1;
      this.currentScale = this.scales[this.scaleIndex] || 'month';
      let nextHourSize = this.hourSizes[this.currentScale] || 1;

      let scaleRatio = nextHourSize / prevHourSize;
      let mouseGraphLeft = this.scrollLeft + mouseLeft;
      let nextMouseGraphLeft = mouseGraphLeft * scaleRatio;
      this.daySize = nextHourSize * 24;
      this.graphWidth = this.dayCount * this.daySize * this.scale;
      this.graphWidth = Math.max(this.graphWidth, this.boardWidth);

      let duration = 0;
      let startScrollLeft = this.scrollLeft;
      let endScrollLeft: number;

      if (positionLeft) {
        endScrollLeft = positionLeft * scaleRatio;
      } else {
        // endScrollLeft = Math.max(Math.floor(nextMouseGraphLeft - mouseLeft), 0);
        const maxScrollLeft = Math.max(0, this.graphWidth - this.boardWidth);
        endScrollLeft = Math.min(Math.max(Math.floor(nextMouseGraphLeft - mouseLeft), 0), maxScrollLeft);
      }

      await this.animateScrollAndSize(
        startScrollLeft,
        endScrollLeft,
        this.hourSize,
        nextHourSize,
        duration
      );

      this.setLines();
    }
  }

  setLateBarWidth(task: PlanningTask) {
    task.lateWidth = 0;

    if (task.doneDate && isBefore(new Date(task.doneDate), new Date(this.currentDate))) {
      task.lateWidth = this.getWidthFromDates(task.endDate!.toString(), task.doneDate.toString())
    } else if (!task.doneDate && task.metrics?.length) {
      let lastMetric = task.metrics[task.metrics.length - 1]
      if (lastMetric)
        task.lateWidth = this.getWidthFromDates(task.endDate!.toString(), lastMetric.payload.endDate!.toString())
      else
        task.lateWidth = 0;

    } else {
      task.lateWidth = 0;
    }
  }

  addTask(parentIndex: number) {
    const parentTask = this.tasks[parentIndex];
    const maxExistingNumber = this.tasksOrder.length ? Math.max(...this.tasksOrder) : 0;
    const newNumber = maxExistingNumber + 1;

    const newTask: PlanningTask = {
      ...this.planningTools.defaultTask,
      costProgress: 0,
      number: newNumber,
      planning: this.planningStore.planning().id,
      target: parentTask?.number?.toString() || '0'
    };

    if (parentTask?.children) {
      parentTask.children.push(newTask);
    }

    this.tasks.splice(parentIndex + 1, 0, newTask);

    const newTaskForService: any = { ...newTask };
    delete newTaskForService.id;
    delete newTaskForService.children;
    delete newTaskForService.parentTask;
    delete newTaskForService.backgroundColor;
    newTaskForService.duration = newTaskForService.duration?.toString();

    this.planningService.addTaskV2(newTaskForService).pipe(take(1)).subscribe({
      next: (res: any) => {
        newTask._id = res.task._id;
        newTask.id = res.task._id;

        const positionInOrder = this.tasksOrder.indexOf(parentTask?.number || 0) + 1;
        this.tasksOrder.splice(positionInOrder, 0, newNumber);
        // this.updateTasksOrder();

        if (this.planningStore.currentTask()?.id === newTask.id) {
          this.planningStore.currentTask.set(newTask);
        }
      },
      error: (error: any) => {
        console.error('Error adding task', error);
      }
    });

    const positionInOrder = this.tasksOrder.indexOf(parentTask?.number || 0) + 1;
    this.tasksOrder.splice(positionInOrder, 0, newNumber);
    this.updateTasksOrder();
  }

  editTask() {
    const taskToSave = { ...this.planningStore.currentTask() };

    if (!taskToSave.startDate) return;

    let endDateNew = new Date(taskToSave.startDate);
    const duration = taskToSave.duration || 0;
    let remainingDuration = duration;

    const schedule = this.project.schedule;
    if (schedule && schedule.mon) {

      const isWorkingDay = (date: Date): boolean => {
        const dayOfWeek = format(date, 'EEE', { locale: enUS }).toLowerCase() as keyof typeof schedule;
        return (schedule as any)[dayOfWeek]?.enable ?? false;
      };

      if (isWorkingDay(endDateNew)) {
        remainingDuration--;
      }

      while (remainingDuration > 0) {
        endDateNew = addDays(endDateNew, 1);

        if (isWorkingDay(endDateNew)) {
          remainingDuration--;
        }
      }

      taskToSave.endDate = endDateNew.toISOString();
    }

    const taskPayload: any = { ...taskToSave, duration: taskToSave.duration?.toString(), progress: taskToSave.progress?.toString() };
    this.planningService.updateTaskV2(taskPayload).subscribe({
      next: (res: any) => {
        console.log('task saves success', res);
      },
      error: (error: any) => {
        console.error('Error task saves', error);
      }
    });
  }

  initializeTasksOrder() {
    if (this.planningStore.planning().tasksOrder?.length) {
      this.tasksOrder = this.planningStore.planning().tasksOrder ?? [];
      return;
    }

    const newOrder = this.tasks.map(task => task.number);

    if (!this.planningStore.planning().tasksOrder || this.planningStore.planning().tasksOrder?.length === 0) {
      this.planningStore.planning().tasksOrder = newOrder;
      this.tasksOrder = newOrder;

      if (newOrder.length > 0) {
        this.updateTasksOrder();
      }
    }
  }

  getOrderedTasks(): void {
    const savedOrder = this.planningStore.planning().tasksOrder;

    if (!savedOrder || savedOrder.length === 0) {
      console.log("Pas d'ordre sauvegardé");
      return;
    }

    if (!this.tasksOrder || this.tasksOrder.length === 0) {
      this.tasksOrder = savedOrder;
    }

    const taskMap = new Map(this.tasks.map(task => [task.number, task]));

    this.tasks = this.tasksOrder
      .map(number => taskMap.get(number))
      .filter((task: PlanningTask | undefined): task is PlanningTask => task !== undefined);

    // this.tasks.set(this.tasks)
  }

  loadMetricsByDate(startDate: string, endDate: string) {
    this.loadedDates.push([startDate, endDate]);

    this.metricService.getMetrics({ startDate, endDate, limit: 10000 }).subscribe(result => {
      let task0 = this.tasks[0];
      if (task0) {
        task0.metrics = [];
      }

      this.planningStore.metrics.set(result.filter(m => m.name == "Coup de grue" && m.payload?.startDate && m.payload?.endDate))

      this.planningStore.metrics().forEach(m => {
        if (m.payload?.startDate && m.payload?.endDate && task0)
          task0.metrics?.push(m);
      })

      this.setTreeGraph();
      this.updateHourSizes();
      this.setZoom(0);
    })
  }

  getCategory(title: string) {
    title = title.toLocaleLowerCase()
    let category = 'other'

    if (title == 'coulage' || title == 'benne à béton') {
      category = 'concreting';
    } else if (title.includes('armature')) {
      category = 'reinforcement';
      // } else if (title.includes('banche') ) {
    } else if (title == 'banche') {
      category = 'formwork';
    } else if (title.includes('préfabriqué') || title.includes('prédalle')) {
      category = 'prefabricated';
    }

    return category;
  }

  async getAttachments(metrics: Metric[]) {
    let promises: Promise<any>[] = [];

    metrics.forEach(m => {
      let promise = new Promise((resolve, reject) => {
        try {
          const attStartDate = typeof m.payload?.startDate === 'string' ? m.payload.startDate : new Date(m.payload?.startDate as any).toISOString();
          const attEndDate = typeof m.payload?.endDate === 'string' ? m.payload.endDate : new Date(m.payload?.endDate as any).toISOString();
          m.attachments = [];

          this.attachmentService.getAttachments({ startDate: attStartDate, endDate: attEndDate, limit: 1, offset: 0, }).pipe(take(1)).subscribe(att => {
            this.attachments = this.attachments.concat(att);
            m.attachments = att;
            resolve(this.attachments);
          });
        } catch (err) {
          console.error(err);
          reject(err);
        }
      });

      promises.push(promise);
    })

    await Promise.all(promises);
  }


  onDragStart(event: DragEvent, index: number): void {
    this.draggedTaskIndex = index;
    event.dataTransfer?.setData('text/plain', index.toString());
  }

  onDragOver(event: DragEvent, index: number): void {
    event.preventDefault();
    const element = (event.target as HTMLElement).closest('.tree-child') as HTMLElement;

    if (!element) {
      this.onDragLeave();
      return;
    }

    if (this.isDragged(index)) {
      event.dataTransfer!.dropEffect = 'none';
      this.dropZoneState = 'none';
      this.hoverIndex = null;
      return;
    }

    const { top, left, height, width } = element.getBoundingClientRect();
    const relativeY = event.clientY - top;
    const relativeX = event.clientX - left;

    const isTop = relativeY < height / 2;
    const isLeft = relativeX < width / 2;

    this.hoverIndex = index;
    this.showDropLine = false;
    this.isNesting = false;

    let newState: 'none' | 'before' | 'after' | 'child-start' | 'child-end';

    if (isLeft && isTop) {
      newState = 'before';
      this.showDropLine = true;
    } else if (isLeft && !isTop) {
      newState = 'after';
      this.showDropLine = true;
    } else if (!isLeft && isTop) {
      newState = 'child-start';
      this.isNesting = true;
    } else {
      newState = 'child-end';
      this.isNesting = true;
    }

    if (this.dropZoneState !== newState) {
      this.dropZoneState = newState;
    }

    event.dataTransfer!.dropEffect = 'move';
  }

  onDragLeave(): void {
    this.showDropLine = false;
    this.hoverIndex = null;
    this.isNesting = false;
    this.dropZoneState = 'none';
  }

  onDragEnd(event: DragEvent | null = null): void {
    this.isDragEnabled = false;
    this.draggedTaskIndex = null;
    this.showDropLine = false;
    this.isNesting = false;
    this.hoverIndex = null;
    this.dropZoneState = 'none';
  }

  startDrag(event: MouseEvent, index: number): void {
    this.isDragEnabled = true;
    this.draggedTaskIndex = index;
    event.stopPropagation();
  }

  stopDrag(): void {
    this.onDragEnd();
  }

  onDrop(event: DragEvent, targetIndex: number): void {
    event.preventDefault();

    if (this.draggedTaskIndex !== null && this.draggedTaskIndex !== targetIndex) {
      const sourceIndex = this.draggedTaskIndex;
      let sourceTask = this.tasks[sourceIndex];

      let getChildren = (task: PlanningTask, childTasks: PlanningTask[] = []): PlanningTask[] => {
        if (!task.children?.length)
          return childTasks;

        childTasks = childTasks.concat(task.children);
        task.children.forEach(t => childTasks = getChildren(t, childTasks));
        return childTasks;
      }

      if (!sourceTask) return;

      let childTasks = getChildren(sourceTask);
      let newParent = this.tasks[targetIndex]?.parent;

      if (this.isNesting) {
        newParent = this.tasks[targetIndex]?.number;
      }

      let targetNumber = this.tasks[targetIndex]?.number;

      ["tasks", "offset", "tasksOrder"].forEach(property => {
        let items = (this as any)[property].splice(sourceIndex, 1 + childTasks.length);

        if (property == "tasks")
          targetIndex = this.tasks.findIndex(t => t.number == targetNumber) + (this.isNesting ? 1 : 0);

        (this as any)[property].splice(targetIndex, 0, ...items);
      });

      if (this.tasks[targetIndex]) {
        const movedTask = this.tasks[targetIndex];

        if (movedTask) {
          movedTask.parent = newParent;

          this.setTreeGraph();
          this.saveTask(sourceTask, false);

          childTasks.forEach(child => {
            this.saveTask(child, false);
          });

          this.saveTask(movedTask, false);
          this.updateTasksOrder();
        }

        this.setLines();
      }

      this.onDragEnd();
    }
  }

  setActiveTask(task: PlanningTask) {

    this.tasks.forEach(l => {
      l.backgroundColor = ""
    })

    task.backgroundColor = "var(--content-background-color-hover)";
  }

  onLineClick(task: PlanningTask) {
    this.isSidebarOpen = true;
    this.setActiveTask(task)
    this.planningStore.setCurrentTask(task);

    this.selectMetrics(task)
    this.selectFragments(task)

    if (task.endDate) {
      this.planningStore.setCurrentDate(task.endDate.toString());
    }
  }

  onMouseEnterBar(event: MouseEvent, line: PlanningTask, posIndex: number) {
    if (!line) return;
    // let data = line[posIndex];
  }

  onMouseLeaveBar() {
    this.darkBarVisible = false;
  }

  onResize(event: CdkDragMove) {
    this.barDeltaX = this.startBarDeltaX + event.distance.x;
    this.dateBarPosition = this.startBarPosition + this.barDeltaX;
  }

  onPickerChanged(event: { startDate: any; endDate: any }) {
    let cTask = this.planningStore.currentTask();

    if (!cTask) return;

    const formatDate = (date: any): string => {
      if (typeof date === 'string')
        return date;

      if (date?.toISOString)
        return date.toISOString();

      return new Date(date).toISOString();
    };

    cTask.startDate = formatDate(event.startDate);
    cTask.endDate = formatDate(event.endDate);
    this.planningStore.currentTask.set(cTask);
    this.editTask();
  }

  onPlayerChanged(event: { startDate: any }) {
    this.updateDateBar((event.startDate?.toISOString?.() ?? event.startDate));
  }

  isDragged(index: number): boolean {
    if (!this.isDragEnabled) return false;
    if (index == this.draggedTaskIndex) return true;

    let task = this.tasks[index];

    if (!task) return false;

    let parent = task.parentTask, isDragged = false;

    do {
      if (this.draggedTaskIndex !== null && parent?.number == this.tasks[this.draggedTaskIndex]?.number) {
        isDragged = true;
        break;
      }

      parent = parent?.parentTask
    } while (parent);
    return isDragged;
  }

  updateTasksOrder(): void {
    this.planningService.updatePlanning({
      id: this.planningStore.planning().id,
      tasksOrder: this.tasksOrder,
      lastUpdate: new Date().toISOString()
    } as any)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.planningStore.updatePlanning({
            ...this.planningStore.planning(),
            tasksOrder: this.tasksOrder,
            lastUpdate: new Date().toISOString()
          });

          this.pop.toast(this.translate.instant("planning.order_updated"), "success", 3000, 'account_tree');
        },
        error: (err) => {
          console.error('❌ Erreur:', err)
          this.pop.toast(this.translate.instant("planning.order_update_failed"), "danger", 3000, 'error');
        }
      });
  }

  saveTask(task: PlanningTask, toastMessage = true): void {
    let query: any = {
      _id: task.id,
      ...task,
      parentTask: undefined, children: undefined,
      fragments: task.fragments,
      fragmentDbIds: task.fragmentDbIds,
      duration: task.duration?.toString(),
      progress: task.progress?.toString()
    }

    this.planningService.updateTask(query).subscribe((data: any) => {
      if (toastMessage && data.action.includes("updated"))
        this.pop.toast(this.translate.instant("planning.task_updated"), "success", 4000, 'save');
    });
  }

  // Is currentScale (e.g. month) smaller than scale argument (e.g. quarter). month is smaller than quarter => yields true
  isSmallerThan(scale: string) {
    let scaleIndex = this.scales.findIndex(s => s == scale);

    if (scaleIndex >= 0)
      return scaleIndex < this.scaleIndex;

    return true;
  }

  // Is currentScale (e.g. month) greater than scale argument (e.g. week). month is greater than week => yields true
  isGreaterThan(scale: string) {
    let scaleIndex = this.scales.findIndex(s => s == scale);

    if (scaleIndex >= 0)
      return scaleIndex > this.scaleIndex;

    return false;
  }

  onZoom(targetScale: string, period: TimelinePeriod): void {
    const zoomSteps = this.scales.indexOf(targetScale) - this.scaleIndex;

    if (zoomSteps !== 0) {
      this.setZoom(zoomSteps, null, null, period.position);
    } else {
      this.scrollLeft = period.position * this.hourSize;
      this.setLines();
    }
  }

  trackByFn(index: number, item: TimelinePeriod): string | number {
    return item.label || index;
  }

  trackByTaskFn(index: number, item: PlanningTask): string | number {
    return item.id || index;
  }

  trackByMetricFn(index: number, item: any): string | number {
    return item.id || item._id || index;
  }
}
